import * as React from 'react';
import { Layout } from '../jsonValidator';
import * as _ from 'lodash';
import { FormEditorContext } from './visualFormEditor';
import { ComponentEditor, ComponentsLibrary, BoolEditor, ErrorWrapper, RemoveLink, StringEditor, ComponentWrapper, VisibleIfEditor, ValidatorEditor, MetaDataEditor, DefaultValueEditor } from './componentsEditor';
import {
	addFieldToGrid,
	addNewComponentToGrid,
	fieldValidator,
	getComponent,
	getComponentPath,
	getField,
	getFieldPath,
	getParentArrayIndex,
	luaInValidatorOrVisibleIfValidator,
	removeFieldFromGrid
} from './formProcessingTools';
import { Translate, translateToNode, translateToString } from '../../../styles/global/translate';
import styled from 'styled-components';
import Popup from '../../../components_v2/popup/Popup';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import useAlert from '../../alert/UseAlert';
import { AlertRes } from '../../alert/AlertProvider';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { Field } from '../../../atoms/forms';
import { LeftModal, ModalData } from '../FormTemplateCreator';
import PopupField from '../PopupField';
import { assert } from '../../../utils/assert';
import Add from '../../../components_v2/add/Add';
import { TitleAndChild } from '../../client-companies/popup/Detail';
import {
	AmberSidely,
	PrimaryColor,
	BorderColor,
	BrightRedSidely,
	BrightTealSidely,
	BrownSidely,
	BurntOrangeSidely,
	CoolGreySidely,
	CoralSidely,
	DarkGreySidely2,
	EmeraldSidely,
	FilterPrimaryColor,
	ForestGreenSidely,
	GreySidely4,
	IndigoSidely,
	LavenderSidely,
	OliveSidely,
	PlumSidely,
	SidelyBlack,
	SiennaSidely,
	SkyBlueSidely,
	SlateSidely,
	SteelBlueSidely,
	SurfaceColor,
	TealSidely,
	YellowSidely
} from '../../../styles/global/css/Utils';
import { FlexDiv } from '../../products/style';
import { Form } from '../create-from-template/type';
import { Checkbox } from '../../../components_v2/filterList/style/Style';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { BadgeDollarSign, Bold, Box, BuildingComplex, Calendar, CalendarDays, CircleChevronDown, CircleUser, ClipboardList, ClipboardType, Clock, EyeOff, File, Globe, Hash, Image as Gallery, Link, ListChecks, Mail, MapPin, Navigation, Pencil, Phone, Route, Settings, SquareCheck, Trash2, Type, User } from 'lucide-react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import DragAndDropDots from '../../../images/icons/drag_and_drop_dots.svg';
import { FieldType } from 'bindings/forms/FieldType';
import Dropdown from '../../../components_v2/dropdown/Dropdown';


const Icon = styled.div`
	cursor: pointer;
	display: inline-block;
`;

function getFieldTypeColor(type: FieldType | undefined): string {
	switch (type) {
		case 'Text': return GreySidely4;
		case 'TextArea': return SlateSidely;
		case 'Number': return PrimaryColor;
		case 'Integer': return AmberSidely;
		case 'Date': return BurntOrangeSidely;
		case 'Period': return BrightRedSidely;
		case 'Boolean': return CoralSidely;
		case 'Select': return IndigoSidely;
		case 'Multiselect': return LavenderSidely;
		case 'File': return ForestGreenSidely;
		case 'Image': return BrightTealSidely;
		case 'Url': return SlateSidely;
		case 'Email': return PlumSidely;
		case 'Phone': return SteelBlueSidely;
		case 'Address': return CoolGreySidely;
		case 'Location': return CoolGreySidely;
		case 'User': return SkyBlueSidely;
		case 'V3User': return SkyBlueSidely;
		case 'Dispatch': return BrownSidely;
		case 'Company': return AmberSidely;
		case 'CheckIn': return OliveSidely;
		case 'Form': return TealSidely;
		case 'Signature': return YellowSidely;
		case 'Money': return EmeraldSidely;
		case 'Geography': return SiennaSidely;
		case 'Entity': return PrimaryColor;
		case 'EntityList': return PrimaryColor;
		default: return GreySidely4;
	}
}

const FieldWrapper = styled.div < { isRow?: boolean, neverVisible?: boolean, fieldType?: FieldType } >`
	width: 100%;
	padding: 8px;
	border-radius: 5px;
	border: 1px solid ${BorderColor};
	${p => p.neverVisible ? `background-color: color-mix(in oklab, ${BorderColor}, ${SurfaceColor} 50%);` : ''}
	${p => p.isRow ? 'height: 100%;' : ''}
	& #field-icons {
		visibility: hidden;
	}
	&:hover {
		& #field-icons {
			visibility: visible;
		}
	}
`;

const ComponentName = styled.div`
	font-size: 14px;
	font-weight: bold;
`;

const BoldSpan = styled.div`
	font-weight: bold;
`;

const InfoText = styled.div`
	font-size: 12px;
	color: ${DarkGreySidely2};
`;

const InlineRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 5px 0;
`;

const LayoutGroupContent = styled.div`
	--border-color:  ${BorderColor};
	width: 100%;
	padding: 0 12px 0 12px;
	margin-left: 12px;
	margin-bottom: 8px;
	border-left: 1px solid var(--border-color);
	&:hover {
		--border-color: ${PrimaryColor};
	}
`;

function EditComponentPopup(props: { path: string, pageNumber: number, name: string }): JSX.Element {
	const { form } = React.useContext(FormEditorContext);
	const component_layout: Layout = _.get(form[props.pageNumber], props.path);
	const pageName = form[props.pageNumber].name;
	if (!pageName || !component_layout.name)
		return <ErrorWrapper><Translate id='form_editor.error_path_prefix' /> {props.path}</ErrorWrapper>;

	const component = getComponent(form, pageName, component_layout.name);

	return <FlexDiv flow='column' align='stretch' overflow='auto' padding='12px' gap='8px' height='100%'>
		<ComponentName>{component_layout.name}</ComponentName>
		{component && <>
			<InfoText><span style={{ fontWeight: 'bold' }}>type:</span> {component.type}</InfoText>
			<InfoText><span style={{ fontWeight: 'bold' }}>slug:</span> {component_layout.name}</InfoText>
		</>}
		<VisibleIfEditor
			path={props.path + '.visible_if'}
			pageNumber={props.pageNumber}
		/>
	</FlexDiv>;

}

function EditFieldPopup(props: { path: string, pageNumber: number, name: string, dbField: Field | undefined }): JSX.Element {
	const { form, advancedMode } = React.useContext(FormEditorContext);
	const field: Layout = _.get(form[props.pageNumber], props.path);
	const pageName = form[props.pageNumber].name;
	const [fieldEditOpen, setFieldEditOpen] = React.useState(false);

	if (!pageName || !field || !field.name || !props.dbField)
		return <ErrorWrapper><Translate id='form_editor.error_path_prefix' /> {props.path}</ErrorWrapper>;

	const fieldPath = getFieldPath(form, pageName, field.name);
	return <FlexDiv flow='column' align='stretch' overflow='auto' padding='12px' gap='12px' height='100%'>
		{props.dbField && <PopupField
			isOpen={fieldEditOpen}
			setIsOpen={setFieldEditOpen}
			field={{ field_id: props.dbField.id }}
			onCreate={() => setFieldEditOpen(false)}
			notAdvancedMode={!advancedMode}
		/>}
		<FlexDiv flow='column' align='stretch' gap='8px'>
			<FlexDiv justify='space-between'>
				<ComponentName>{props.dbField.name}</ComponentName>
				<FlexDiv cursor='pointer' padding='5px' justify='center' width='30px' style={{ aspectRatio: 1 }} onClick={() => setFieldEditOpen(true)}>
					<Settings size={16} />
				</FlexDiv>
			</FlexDiv>
			<InfoText><span style={{ fontWeight: 'bold' }}>type:</span> {props.dbField.type}</InfoText>
			{advancedMode && <InfoText><span style={{ fontWeight: 'bold' }}>slug:</span> {field.name}</InfoText>}
		</FlexDiv>

		<FlexDiv flow='column' align='stretch' gap='4px'>
			<BoldSpan><Translate id='form_editor.visibility' /></BoldSpan>
			<VisibleIfEditor
				path={props.path + '.visible_if'}
				pageNumber={props.pageNumber}
			/>
		</FlexDiv>

		<FlexDiv flow='column' align='stretch' gap='4px'>
			<BoldSpan><Translate id='form_editor.required_field' /></BoldSpan>
			<ValidatorEditor
				path={fieldPath + ''}
				pageNumber={props.pageNumber}
			/>
		</FlexDiv>

		<FlexDiv flow='column' align='stretch' gap='4px'>
			<BoldSpan><Translate id='form_editor.value_label' /></BoldSpan>
			<InlineRow>
				<BoolEditor path={fieldPath + '.history'} pageNumber={props.pageNumber} />
				<Translate id='form_editor.historise' />
			</InlineRow>
			<DefaultValueEditor
				path={fieldPath + '.default'}
				pageNumber={props.pageNumber}
				field={props.dbField}
			/>
			{advancedMode &&
				<div>
					<div><Translate id='form_editor.retrieve_via_function' /></div>
					<StringEditor
						path={fieldPath + '.calculated_value'}
						pageNumber={props.pageNumber}
					/>
				</div>
			}
		</FlexDiv>

		{advancedMode &&
			<FlexDiv flow='column' align='stretch' gap='4px'>
				<BoldSpan><Translate id='form_editor.metadata_label' /></BoldSpan>
				<MetaDataEditor
					path={fieldPath + '.metadata'}
					pageNumber={props.pageNumber}
				/>
			</FlexDiv>
		}

	</FlexDiv>;
}

function get_default_metadata(form: Form, pageNumber: number): string[] {
	const metadata: string[] = [];
	form[pageNumber].fields?.forEach(field => {
		if (field.metadata) {
			Object.keys(field.metadata).forEach(key => {
				if (!metadata.includes(key)) {
					metadata.push(key);
				}
			});
		}
	});

	return metadata;
}

function get_available_metadata(form: Form): { key: string, value: any }[] {
	const metadata: { key: string, value: any }[] = [];
	form.forEach(page => {
		page.fields?.forEach(field => {
			if (field.metadata) {
				Object.keys(field.metadata).forEach((key) => {
					if (!metadata.find(m => m.key === key)) {
						metadata.push({ key, value: field.metadata[key] });
					}
				});
			}
		});
	});

	form.forEach(page => {
		page.components?.forEach(component => {
			switch (component.type) {
				case 'catalogue_dispatcher':
					if (metadata.find(m => m.key === 'catalogue') === undefined) {
						metadata.push({ key: 'catalogue', value: null });
					}
					break;
				case 'product_dispatcher':
					if (metadata.find(m => m.key === 'product') === undefined) {
						metadata.push({ key: 'product', value: null });
					}
					break;
			}
		});
	});
	return metadata;
}

function SelectMetadata(props: {
	metadataList: { key: string, value: any }[] | undefined, metadataChecked: string[] | undefined, setChecked: (metadata: string[]) => void
}): JSX.Element {
	return <>
		{props.metadataList?.map((metadata, index) => {

			return <div key={index}>
				<FlexDiv gap='5px'>
					<Checkbox style={{ marginRight: '5px' }}
						isActive={props.metadataChecked?.includes(metadata.key) ?? false}
						onClick={() => {
							if (props.metadataChecked?.includes(metadata.key)) {
								props.setChecked(props.metadataChecked.filter(m => m !== metadata.key));
							} else {
								props.setChecked([...(props.metadataChecked ?? []), metadata.key]);
							}
						}}
					/>
					<span>{metadata.key}</span>
				</FlexDiv>
			</div>;
		})}
	</>;
}


const DarkBackground = styled.div<{ $open: boolean }>`
	display: ${props => props.$open ? 'block' : 'none'};
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: color-mix(in oklab, ${SidelyBlack} 50%, transparent);
	z-index: 110;
`;

const MetadataPopupWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding: 20px;
	font-size: 14px;
`;

const ButtonWrapper = styled.div`
	align-self: center;
`;

const MetadataTitle = styled.span`
	padding-bottom: 10px;
	border-bottom: 1px solid ${BorderColor};
	font-size: 15px;
	font-weight: bold;
`;

const BoldTitle = styled.span`
	font-weight: bold;
`;

const ComponentGroup = styled.div < { border: boolean }>`
	font-size: 13px;
	width: 100%;
	border-radius: 5px;
	padding-left: 5px;
	${p => p.border ? `border : 1px solid ${BorderColor};` : ''}
`;

export function AddToLayoutAndFields(props: { path: string, pageNumber: number, layout: Layout, fieldList: Field[], addTo?: string }): JSX.Element {
	const [addFieldPopupOpen, setaddFieldPopupOpen] = React.useState<boolean>(false);
	const [addComponentPopupOpen, setaddComponentPopupOpen] = React.useState<boolean>(false);
	const [newFieldModalState, setNewFieldModalState] = React.useState<{ isOpen: boolean, data?: ModalData }>({ isOpen: false });
	const [metadataFromNewField, setMetadataFromNewField] = React.useState<boolean>(false);
	const { form, setForm, advancedMode, appendField } = React.useContext(FormEditorContext);

	const [selectMetadataPopup, setSelectMetadataPopup] = React.useState<boolean>(false);

	const pageName = form[props.pageNumber].name;

	const [selectedField, setSelectedField] = React.useState<Field | undefined>();

	const [metadataList, setMetadataList] = React.useState<{ key: string, value: any }[]>();
	const [metadataChecked, setChecked] = React.useState<string[]>();

	React.useEffect(() => {

		setMetadataList(get_available_metadata(form));
		setChecked([...new Set(get_default_metadata(form, props.pageNumber))]);
	}, [props.pageNumber]);

	return <>
		<DarkBackground $open={addFieldPopupOpen} />
		{newFieldModalState.data && <PopupField
			isOpen={newFieldModalState.isOpen}
			setIsOpen={isOpen => setNewFieldModalState({ isOpen })}
			field={newFieldModalState.data}
			onCreate={(field) => {
				setNewFieldModalState({ isOpen: false });
				if (!field) return;
				appendField(field);
				if (!advancedMode) {
					if (pageName) {
						const newForm = _.cloneDeep(form);
						setForm(addFieldToGrid(newForm, pageName, {
							slug: field.slug,
							metadata: (metadataChecked ?? []).reduce((acc, key) => {
								acc[key] = metadataList?.find(m => m.key === key)?.value;
								return acc;
							}, {})
						}, props.path));
					}
				} else {
					setSelectedField(field);
					setMetadataFromNewField(true);
					setSelectMetadataPopup(true);
				}
			}}
		/>}
		<Popup isOpen={selectMetadataPopup} popupStyle={{ top: '0', left: metadataFromNewField ? '73vw' : '49vw', height: '100vh', width: '27vw', animate: true, boxShadow: `0px 0px 16px color-mix(in oklab, ${SidelyBlack} 6%, transparent)` }} onClickOut={() => { setSelectMetadataPopup(false); setMetadataFromNewField(false); }}
			content={<MetadataPopupWrapper>
				<MetadataTitle>{selectedField?.name}</MetadataTitle>

				<span><Translate id='form_editor.metadata_label' /></span>
				<SelectMetadata
					metadataList={metadataList}
					metadataChecked={metadataChecked}
					setChecked={setChecked}
				/>

				<ButtonWrapper>
					<DefaultButton buttonStyle={ButtonStyle.White} width='9vw' onClick={() => { setSelectMetadataPopup(false); setMetadataFromNewField(false); }}><Translate id={'cancel'} /></DefaultButton>
					<DefaultButton buttonStyle={ButtonStyle.Default} width='9vw' onClick={() => {
						if (pageName && selectedField) {
							const newForm = _.cloneDeep(form);
							setForm(addFieldToGrid(newForm, pageName, {
								slug: selectedField.slug,
								metadata: (metadataChecked ?? []).reduce((acc, key) => {
									acc[key] = metadataList?.find(m => m.key === key)?.value;
									return acc;
								}, {})
							}, props.path));
						}
						setSelectMetadataPopup(false);
						setMetadataFromNewField(false);
					}}><Translate id={'add'} /></DefaultButton>
				</ButtonWrapper>
			</MetadataPopupWrapper>} />
		<Popup
			popupStyle={{ noTransition: true }}
			isOpen={addFieldPopupOpen}
			popupMode={PopupMode.Details}
			onClickOut={() => setaddFieldPopupOpen(false)}
			content={<>
				<LeftModal externalUse={true}
					creationNotAllowed={true}
					allowEdit={false}
					onFieldClicked={(f) => {
						setSelectedField(f);
						setSelectMetadataPopup(true);
					}}
					errors={[]}
					advancedMode={advancedMode}
				/>
			</>}
		/>

		<Popup
			popupStyle={{ noTransition: true }}
			isOpen={addComponentPopupOpen}
			popupMode={PopupMode.Details}
			onClickOut={() => setaddComponentPopupOpen(false)}
			content={<FlexDiv flow='column' align='stretch' gap='8px' padding='12px' overflow='auto' height='100%'>
				<BoldTitle><Translate id='form_editor.create_new_component' /></BoldTitle>
				<ComponentsLibrary
					advancedMode={advancedMode}
					setComponent={(component) => {
						if (pageName && component && component.name) {
							const newForm = _.cloneDeep(form);
							setForm(addNewComponentToGrid(newForm, pageName, component, props.path));
							setaddComponentPopupOpen(false);
						}
					}}
				/>
			</FlexDiv>}
		/>
		<Dropdown
			searchable={false}
			name='add-field'
			dropdownStyle={{ optionWidth: 'fit-content', containerTop: '-100px' }}
			datalist={[{ label: translateToString('form_editor.field_label'), value: 'add_field' }, { label: translateToString('form_editor.new_field_label'), value: 'add_new_field' }, { label: translateToString('form_editor.component_label'), value: 'add_component' }]}
			JSXButton={() => <Add size='20px' />}
			onChange={(value) => {
				switch (value.value) {
					case 'add_field':
						setaddFieldPopupOpen(true);
						break;
					case 'add_new_field':
						setNewFieldModalState({ isOpen: true, data: {} });
						break;
					case 'add_component':
						setaddComponentPopupOpen(true);
						break;
				}
			}}
		/>
	</>;
}

const EditChildrenLayoutWrapper = styled.div<{ isRow?: boolean, columnCount?: number }>`
	${p => p.isRow ? `
		display: grid;
		grid-template-columns: repeat(${p.columnCount ?? 1}, minmax(150px, 1fr));
		grid-template-rows: 1fr;
		gap: 8px;
	`
		:
		''
}
`;

const DragHandleBar = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 2px;
	cursor: grab;
	flex-shrink: 0;
	transition: filter 0.15s;
	&:hover { filter: ${FilterPrimaryColor}; }
	&:active { cursor: grabbing; }
`;

function EditChildrenLayout(props: { path: string, pageNumber: number, layout: Layout, fieldList: Field[], isRow?: boolean }): JSX.Element {
	if (props.layout.children !== undefined) {
		return <FlexDiv flow='column' align='stretch'>
			<Droppable droppableId={props.path} direction={props.isRow ? 'horizontal' : 'vertical'}>
				{(provided) => (
					<div ref={provided.innerRef} {...provided.droppableProps}>
						<EditChildrenLayoutWrapper isRow={props.isRow} columnCount={props.layout.children!.length}>
							{
								props.layout.children!.map((child, index) => (
									<Draggable key={`${props.path}[${index}]`} draggableId={`${props.path}[${index}]`} index={index}>
										{(provided) => (
											<div
												ref={provided.innerRef}
												{...provided.draggableProps}
												style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', ...provided.draggableProps.style }}
											>
												<DragHandleBar {...provided.dragHandleProps}>
													<img src={DragAndDropDots} />
												</DragHandleBar>
												<div style={{ flex: 1, minWidth: 0 }}>
													<EditLayout path={`${props.path}[${index}]`}
														pageNumber={props.pageNumber}
														layout={child}
														fieldList={props.fieldList}
														isRow={props.isRow}
													/>
												</div>
											</div>
										)}
									</Draggable>
								))
							}
							{provided.placeholder}
						</EditChildrenLayoutWrapper>
					</div>
				)}
			</Droppable>
			<div>
				<AddToLayoutAndFields path={props.path} pageNumber={props.pageNumber} layout={props.layout} fieldList={props.fieldList} addTo={props.layout.name} />
			</div>
		</FlexDiv>;
	}
	else {
		return <>
			<ErrorWrapper><Translate id='form_editor.error_component_check_code' /></ErrorWrapper>
		</>;
	}
}

function EditSingleChildLayout(props: { path: string, pageNumber: number, layout: Layout, fieldList: Field[] }): JSX.Element {
	assert(props.layout.child !== undefined, 'child is undefined');
	return <>
		<EditLayout path={props.path}
			pageNumber={props.pageNumber}
			layout={props.layout.child}
			fieldList={props.fieldList}
		/>
	</>;
}

const HiddenFieldWrapper = styled.div < { message: string }>`
	cursor: help;
	position: relative;
	&:hover {
		&:after {
			background-color: ${SurfaceColor};
			padding: 5px;
			border: 1px solid ${BorderColor};
			border-radius: 5px;
			position: absolute;
			top: 30px;
			left: -200%;
			content: '${p => p.message}';
			text-wrap-mode: nowrap;
		}
	}
`;

function FieldTypeIcon({ type, size, style }: { type: FieldType, size: number, style?: React.CSSProperties }): JSX.Element {
	const color = getFieldTypeColor(type);
	switch (type) {
		case 'Text': return <Type size={size} color={color} style={style} />;
		case 'TextArea': return <ClipboardType size={size} color={color} style={style} />;
		case 'Number': return <div style={{ ...style, color, fontSize: size, width: size, textAlign: 'center' }}>π</div>;
		case 'Integer': return <Hash size={size} color={color} style={style} />;
		case 'Date': return <Calendar size={size} color={color} style={style} />;
		case 'Period': return <CalendarDays size={size} color={color} style={style} />;
		case 'Boolean': return <SquareCheck size={size} color={color} style={style} />;
		case 'Select': return <CircleChevronDown size={size} color={color} style={style} />;
		case 'Multiselect': return <ListChecks size={size} color={color} style={style} />;
		case 'File': return <File size={size} color={color} style={style} />;
		case 'Image': return <Gallery size={size} color={color} style={style} />;
		case 'Url': return <Link size={size} color={color} style={style} />;
		case 'Email': return <Mail size={size} color={color} style={style} />;
		case 'Phone': return <Phone size={size} color={color} style={style} />;
		case 'Address': return <MapPin size={size} color={color} style={style} />;
		case 'Location': return <Navigation size={size} color={color} style={style} />;
		case 'User': return <User size={size} color={color} style={style} />;
		case 'V3User': return <CircleUser size={size} color={color} style={style} />;
		case 'Dispatch': return <Route size={size} color={color} style={style} />;
		case 'Company': return <BuildingComplex size={size} color={color} style={style} />;
		case 'CheckIn': return <Clock size={size} color={color} style={style} />;
		case 'Form': return <ClipboardList size={size} color={color} style={style} />;
		case 'Signature': return <Pencil size={size} color={color} style={style} />;
		case 'Money': return <BadgeDollarSign size={size} color={color} style={style} />;
		case 'Geography': return <Globe size={size} color={color} style={style} />;
		case 'Entity': return <Box size={size} color={color} style={style} />;
		case 'EntityList': return <ListChecks size={size} color={color} style={style} />;
		default: return <Bold size={size} color={color} style={style} />;
	}
}

function EditSimpleLayout(props: { path: string, pageNumber: number, layout: Layout, fieldList: Field[], isRow?: boolean, neverVisible?: boolean }): JSX.Element {
	const alert = useAlert();
	const { form, setForm } = React.useContext(FormEditorContext);
	const pageName = form[props.pageNumber].name;
	const [editFieldOpen, setEditFieldOpen] = React.useState<boolean>(false);


	const fieldDB = props.fieldList.find(f => f.slug === getField(form, pageName ?? '', props.layout.name ?? '')?.slug);

	return <>
		{props.layout.name && <Popup
			popupStyle={{ noTransition: true }}
			isOpen={(props.layout.name !== undefined) && editFieldOpen}
			popupMode={PopupMode.Details}
			onClickOut={() => setEditFieldOpen(false)}
			content={<EditFieldPopup path={props.path} pageNumber={props.pageNumber} name={props.layout.name} dbField={fieldDB} />}
		/>}
		<FlexDiv justify={'space-between'} height='100%' minHeight={props.isRow ? '50px' : undefined} position='relative'>
			{fieldDB && <FlexDiv gap='8px'>
				<FieldTypeIcon type={fieldDB.type} size={16} style={{ flexShrink: '0' }} />
				<div>{fieldDB.name}</div>
				{props.neverVisible &&
					<HiddenFieldWrapper message={translateToString('form_editor.hidden_field')}><EyeOff size={16} color={GreySidely4} /></HiddenFieldWrapper>
				}
			</FlexDiv>}
			{pageName && props.layout.name && getField(form, pageName, props.layout.name)?.validator && !luaInValidatorOrVisibleIfValidator(form, pageName, getField(form, pageName, props.layout.name)?.validator) && <ErrorWrapper><Translate id='form_editor.error_validator_prefix' /> {getField(form, pageName, props.layout.name)?.validator} <Translate id='form_editor.error_not_valid_suffix' /></ErrorWrapper>}
			{pageName && props.layout.name && props.layout.visible_if && !luaInValidatorOrVisibleIfValidator(form, pageName, props.layout.visible_if) && <ErrorWrapper><Translate id='form_editor.error_visible_if_prefix' /> {props.layout.visible_if} <Translate id='form_editor.error_not_valid_suffix' /></ErrorWrapper>}
			<FlexDiv id='field-icons' gap='4px' position={props.isRow ? 'absolute' : undefined} style={props.isRow ? { top: '0', right: '0' } : undefined} backgroundColor={props.isRow ? SurfaceColor : undefined}>
				<Icon
					onClick={
						() => setEditFieldOpen(true)
					}
				>
					<Settings size={16} />
				</Icon>
				<Icon
					onClick={
						() => {
							alert({
								title: translateToNode('import.are_you_sure'),
								content: <>{translateToString('form_editor.delete_field')}{props.layout.name} ?</>,
								buttons: [
									{
										title: translateToNode('cancel'),
										res: AlertRes.Break,
										style: ButtonStyle.White
									},
									{
										title: translateToNode('yes'),
										res: AlertRes.Ok,
										style: ButtonStyle.Error
									}
								]
							}).then(res => {
								if (pageName && props.layout.name && res.res === AlertRes.Ok) {
									setForm(removeFieldFromGrid(form, props.pageNumber, props.layout.name, props.path));
								}
							});
						}
					}
				>
					<Trash2 size={16} />
				</Icon>
			</FlexDiv>
		</FlexDiv>
	</>;
}

export function EditLayout(props: { path: string, pageNumber: number, layout: Layout, fieldList: Field[], isRow?: boolean }): JSX.Element {
	const { form, setForm } = React.useContext(FormEditorContext);
	const pageName = form[props.pageNumber].name;
	const fieldName = props.layout?.name ?? 'no name';
	const alert = useAlert();
	const [editFieldOpen, setEditFieldOpen] = React.useState<boolean>(false);
	const [editGroupOpen, setEditGroupOpen] = React.useState<boolean>(false);
	const [groupTitleHovered, setGroupTitleHovered] = React.useState<boolean>(false);

	if (props.layout) {
		switch (props.layout.type) {
			case 'field': {
				const fieldDB = props.fieldList.find(f => f.slug === getField(form, pageName, fieldName)?.slug);
				if (pageName && fieldName && fieldValidator(form, pageName, fieldName)) {
					return <FieldWrapper isRow={props.isRow} neverVisible={props.layout.visible_if === 'false'} fieldType={fieldDB?.type}>
						<EditSimpleLayout
							path={props.path}
							pageNumber={props.pageNumber}
							layout={props.layout}
							fieldList={props.fieldList}
							isRow={props.isRow}
							neverVisible={props.layout.visible_if === 'false'}
						/>
					</FieldWrapper>;
				} else {
					return <ErrorWrapper><Translate id='form_editor.error_field_not_existing_pre' /> {fieldName} <Translate id='form_editor.error_field_not_existing_post' /> <RemoveLink
						onClick={() => {
							if (fieldName)
								setForm(removeFieldFromGrid(form, props.pageNumber, fieldName, props.path));
						}}
					><Translate id='form_editor.not_valid.remove_it' /></RemoveLink></ErrorWrapper>;
				}
			}
			case 'container':
			case 'expanded_section':
				return <>
					<EditSingleChildLayout
						path={`${props.path}.child`}
						pageNumber={props.pageNumber}
						layout={props.layout}
						fieldList={props.fieldList}
					/>
				</>;
			case 'row':
			case 'column': {
				const isRow = props.layout.type === 'row';
				return <ComponentGroup style={isRow ? { overflow: 'auto' } : undefined} border={getParentArrayIndex(form, props.pageNumber, props.path) != -1}>
					<Popup
						popupStyle={{ noTransition: true }}
						isOpen={editGroupOpen}
						popupMode={PopupMode.Details}
						onClickOut={() => setEditGroupOpen(false)}
						content={<FlexDiv flow='column' align='stretch' padding='12px' gap='8px'>
							<BoldSpan><Translate id='form_editor.group_name' /></BoldSpan>
							<StringEditor path={props.path + '.name'} pageNumber={props.pageNumber} />
						</FlexDiv>}
					/>
					<TitleAndChild
						defaultOpen={getParentArrayIndex(form, props.pageNumber, props.path) == -1}
						titleStyle={{ margin: '5px', minWidth: '335px' }}
						title={<FlexDiv
							gap='8px'
							justify='space-between'
							width='100%'
							fontWeight='400'
							onMouseEnter={() => setGroupTitleHovered(true)}
							onMouseLeave={() => setGroupTitleHovered(false)}
						>
							{props.layout.name ?? <Translate id='form_editor.field_and_component_group' />}
							<div style={{ visibility: groupTitleHovered ? 'visible' : 'hidden' }}>
								<Icon onClick={(e) => { e.stopPropagation(); setEditGroupOpen(true); }}>
									<Settings size={16} />
								</Icon>
							</div>
						</FlexDiv>}
					>
						<LayoutGroupContent>
							<EditChildrenLayout
								path={`${props.path}.children`}
								pageNumber={props.pageNumber}
								layout={props.layout}
								fieldList={props.fieldList}
								isRow={isRow}
							/>
						</LayoutGroupContent>
					</TitleAndChild>
				</ComponentGroup>;
			}
			case 'component': {
				const component_path = getComponentPath(form, props.pageNumber, props.layout.name);
				const component = getComponent(form, pageName, props.layout.name);

				if (!component_path || !component) {
					return <ErrorWrapper>
						<Translate id='form_editor.error_component_not_found_pre' /> {props.layout.name} <Translate id='form_editor.error_component_not_found_post' /> <RemoveLink
							onClick={() => {
								if (props.layout.name)
									setForm(removeFieldFromGrid(form, props.pageNumber, props.layout.name, props.path));
							}}
						><Translate id='form_editor.not_valid.remove_it' /></RemoveLink>
					</ErrorWrapper>;
				} else {
					return <>
						{props.layout.name && <Popup
							popupStyle={{ noTransition: true }}
							isOpen={(props.layout.name !== undefined) && editFieldOpen}
							popupMode={PopupMode.Details}
							onClickOut={() => setEditFieldOpen(false)}
							content={<EditComponentPopup path={props.path} pageNumber={props.pageNumber} name={props.layout.name} />}
						/>}
						<FlexDiv>
							<ComponentWrapper>
								<FlexDiv
									id='setting-icons'
									style={{ position: 'absolute', top: '5px', right: '5px' }}
									gap='4px'
								>
									<div style={{ cursor: 'pointer' }} >
										<Settings size={16}
											onClick={ev => {
												ev.stopPropagation();
												setEditFieldOpen(true);
											}}
										/>
									</div>
									<div
										style={{ cursor: 'pointer' }}
										onClick={ev => {
											ev.stopPropagation();
											alert({
												title: translateToNode('import.are_you_sure'),
												content: <>{translateToString('form_editor.delete_component')}{props.layout.name} ?</>,
												buttons: [
													{
														title: translateToNode('cancel'),
														res: AlertRes.Break,
														style: ButtonStyle.White
													},
													{
														title: translateToNode('yes'),
														res: AlertRes.Ok,
														style: ButtonStyle.Error
													}
												]
											}).then(res => {
												if (pageName && fieldName && res.res === AlertRes.Ok) {
													setForm(removeFieldFromGrid(form, props.pageNumber, fieldName, props.path));
												}
											});
										}}
									><Trash2 size={16} />
									</div>

								</FlexDiv>
								{pageName && !luaInValidatorOrVisibleIfValidator(form, pageName, props.layout.visible_if) && <ErrorWrapper>
									<Translate id='form_editor.error_visible_if_prefix' /> {props.layout.visible_if} <Translate id='form_editor.error_not_valid_suffix' />
								</ErrorWrapper>}
								{component_path && component && <ComponentEditor
									path={component_path}
									pageNumber={props.pageNumber}
									component={component}
								/>}
							</ComponentWrapper>
						</FlexDiv >
					</>;
				}
			}
			default:
				return <><Translate id='form_editor.unknown_layout_type' /></>;
		}
	} else {
		return <><Translate id='form_editor.no_layout_prefix' /> <RemoveLink onClick={() => {
			const newForm = _.cloneDeep(form);
			_.set(newForm[props.pageNumber], props.path, {
				'children': [],
				'type': 'column'
			});
			setForm(newForm);
		}}><Translate id='form_editor.click_to_create_layout' /></RemoveLink></>;
	}
}
